/**
 * AlphaSense Research Agent
 *
 * Sub-agent for searching AlphaSense financial research data.
 * Implements the Agent-as-Tool pattern via AgentBaseClass.
 *
 * ## Capabilities
 * - Search earnings call transcripts
 * - Query broker research and equity reports
 * - Access expert call transcripts
 * - Retrieve company presentations
 * - Use FinData agent for structured financial data
 *
 * ## Chunk-based Storage
 * Responses are parsed into logical chunks with short IDs (aschunk_*) for
 * efficient token passing between agents. Each chunk contains:
 * - Section content (markdown)
 * - Associated citations
 * - Header if present
 *
 * ## Usage
 * ```typescript
 * const result = await alphaSenseAgent.execute({
 *     requirements: [
 *         { id: 'A', requirement: 'Find Apple Q4 revenue data and YoY growth' }
 *     ],
 *     context: []  // Optional previous context
 * }, callbacks);
 * ```
 */
import { AgentBaseClass } from "../../base/agentBaseClass";
import { ToolDescription } from "../../base/toolDescription";
import { AlphaSenseParamsSchema } from "./alphaSense.schema";
import { AlphaSenseSearchTool } from "./tools";
import GlobalStateManager from "../../../globals/global-state/globalStateManager";
import { buildAlphaSenseSystemPrompt } from "./prompt";
// ═══════════════════════════════════════════════════════════════════════════
// ALPHASENSE AGENT
// ═══════════════════════════════════════════════════════════════════════════
// System prompt for agent-as-tool description (stored in Keywords AI)
const ALPHASENSE_AGENT_TOOL_PROMPT = `Search AlphaSense for financial research data.

Use this agent when you need:
- Earnings call transcripts and management commentary
- Broker research and equity analyst reports
- Expert call transcripts and industry insights
- Company presentations and investor materials
- SEC filings and financial data
- Market trends and competitive analysis

The agent will search AlphaSense and return relevant excerpts with citations.
Results are stored as chunks with contextId field (as_*) for token-efficient passing.`;
/**
 * AlphaSense Research Agent
 *
 * Searches AlphaSense for financial research data including:
 * - Earnings call transcripts
 * - Broker research reports
 * - Expert calls
 * - Company presentations
 * - SEC filings
 */
export class AlphaSenseAgent extends AgentBaseClass {
    constructor() {
        super();
        // ═══════════════════════════════════════════════════════════════════════
        // AGENT-AS-TOOL PROMPT CONFIGURATION
        // ═══════════════════════════════════════════════════════════════════════
        /** System prompt description for agent-as-tool */
        this.systemPromptDescription = ALPHASENSE_AGENT_TOOL_PROMPT;
        // ═══════════════════════════════════════════════════════════════════════
        // REQUIRED IMPLEMENTATIONS
        // ═══════════════════════════════════════════════════════════════════════
        this.agentType = 'alphasense';
        this.name = 'alphaSenseSearchAgent';
        this.isAgent = true; // Mark as agent-as-tool
        this.createsUndoCheckpoint = false; // Read-only search agent
        // Tools this agent can use
        this.toolCalls = [
            new AlphaSenseSearchTool(),
        ];
        // ═══════════════════════════════════════════════════════════════════════
        // STREAMING FIELD CONFIGURATION (for Input Parameters UI)
        // ═══════════════════════════════════════════════════════════════════════
        /** Define fields to extract during streaming for progressive UI updates */
        this.fieldsToCheck = [
            { name: 'preferFinData', type: 'boolean', list_status: false, order: 2 },
        ];
        /** Define list fields - items committed individually as they stream in */
        this.listsToCheck = [
            {
                name: 'requirements',
                type: 'array',
                item_type: 'object',
                list_status: true,
                order: 1,
                check_sub_field: 'requirement' // Item ready when 'requirement' field exists
            },
        ];
        // Enable infrastructure for agent-as-tool pattern
        this.useThreadManagement = true;
        this.useTaskTracking = true;
        this.useContextResolution = true;
        // Enable streaming for real-time UI updates
        this.stream = true;
        // Allow longer responses for detailed research analysis
        // maxResponseTokens: inherits 0 from base → adapter uses model maximum
        // Track last search response for result processing
        this.lastSearchResponse = null;
        this.description = new ToolDescription(this.name, this.systemPromptDescription, AlphaSenseParamsSchema);
        // Set paramsSchema for agent-as-tool pattern
        this.paramsSchema = AlphaSenseParamsSchema;
        // Initialize agent (injects submitResults tool)
        this.initializeAgent();
    }
    /**
     * Generate system prompt for the AlphaSense agent
     * Uses the new standardized 5-section prompt structure
     */
    generateSystemPrompt(resolvedContext) {
        return buildAlphaSenseSystemPrompt({ resolvedContext, tools: this.toolCalls });
    }
    // ═══════════════════════════════════════════════════════════════════════
    // TOOL RESULT HANDLING
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Handle tool results - called via callback during tool loop.
     * Handles batch responses with multiple query results.
     */
    handleToolResult(toolName, result) {
        if (toolName === 'searchAlphaSense' && result) {
            this.lastSearchResponse = result;
            // Log batch statistics
            const stats = this.lastSearchResponse.stats;
            console.log(`[AlphaSenseAgent] Batch search complete: ${stats.successfulQueries}/${stats.totalQueries} queries succeeded`);
            // Store chunks from all query results
            // The response now has queryResults array with per-query chunks
            for (const queryResult of this.lastSearchResponse.queryResults) {
                if (queryResult.error) {
                    console.warn(`[AlphaSenseAgent] Query failed: "${queryResult.query.slice(0, 50)}..." - ${queryResult.error}`);
                    continue;
                }
                const conversationId = queryResult.conversation_id || '';
                this.storeChunks(queryResult.chunks, conversationId);
                console.log(`[AlphaSenseAgent] Stored ${queryResult.chunks.length} chunks for query: "${queryResult.query.slice(0, 40)}..."`);
            }
            console.log(`[AlphaSenseAgent] Total chunks stored: ${this.lastSearchResponse.chunks.length}`);
        }
    }
    /**
     * Store chunks in context resolver for later retrieval
     */
    storeChunks(chunks, conversationId) {
        for (const chunk of chunks) {
            const storedData = {
                id: chunk.contextId,
                text: chunk.content,
                citationsUsed: chunk.citations.map(c => ({
                    citationNumber: c.id,
                    citationFullTitle: c.title || `Source ${c.id}`,
                    citationUrl: c.url,
                })),
                conversationId,
                createdAt: new Date().toISOString(),
            };
            // Store using the chunk's context ID directly in the resolver
            GlobalStateManager.getInstance().addOrUpdateContext(chunk.contextId, storedData);
        }
    }
    // ═══════════════════════════════════════════════════════════════════════
    // HOOKS - Override base class hooks for AlphaSense-specific behavior
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Prepare for execution - clear previous search state
     */
    async beforeExecute(context) {
        console.log(`[AlphaSenseAgent] Starting execution with ${context.params.requirements.length} requirements`);
        this.lastSearchResponse = null;
    }
    /**
     * Capture tool results - specifically AlphaSense search responses
     */
    onToolResult(toolName, result) {
        this.handleToolResult(toolName, result);
    }
    /**
     * Build final result with context ID note for orchestrator
     */
    buildFinalResult(results, threadId, tokensUsed, summary) {
        if (results.length === 0) {
            return this.buildPartialResult(results, threadId, tokensUsed, summary);
        }
        const result = this.buildResult(results, threadId, tokensUsed, summary);
        result._contextIdNote = "Use the 'contextId' field from chunks when calling submitResults (e.g., 'as_abc123'). These contextId values can also be passed to other agents in the 'context' parameter for efficient token usage.";
        return result;
    }
    // ═══════════════════════════════════════════════════════════════════════
    // RESULT PROCESSING (Submit Results Pattern)
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Process results from the submitResults tool.
     * This is the primary path when using the Submit Results Pattern.
     *
     * Resolves short IDs (as_*) to full content with citations.
     */
    async processSubmittedResults(submittedResults, _params, context) {
        const results = [];
        for (const submitted of submittedResults.results) {
            // Check cancellation
            context.cancellationToken.throwIfCancelled();
            // Extract chunk IDs from the submitted relevantResults
            const searchResultsField = submitted.relevantResults?.searchResults;
            const chunkIds = Array.isArray(searchResultsField)
                ? searchResultsField
                : [];
            // Resolve the short IDs to full content
            const resolvedSearchResults = this.resolveSearchResults(chunkIds);
            results.push({
                requirementId: submitted.requirementId,
                relevantResults: {
                    searchResults: resolvedSearchResults,
                },
                rationale: submitted.rationale,
            });
            console.log(`[AlphaSenseAgent] Requirement ${submitted.requirementId}: ${resolvedSearchResults.length} resolved chunks from submitResults`);
        }
        return results;
    }
    /**
     * Legacy: Process the LLM response and extract structured results.
     * This is a fallback for when submitResults is not called.
     */
    async processResults(responseContent, params, context) {
        console.warn('[AlphaSenseAgent] processResults called - LLM did not use submitResults tool');
        const results = [];
        const requirements = params.requirements;
        // Get all chunk IDs from the last search response
        const allChunkIds = this.lastSearchResponse?.chunks.map(c => c.contextId) || [];
        // For each requirement, extract relevant findings from the response
        for (const req of requirements) {
            // Check cancellation
            context.cancellationToken.throwIfCancelled();
            // Extract chunk IDs mentioned in the LLM response for this requirement
            const referencedChunkIds = this.extractChunkIdsFromResponse(responseContent, allChunkIds);
            // If LLM didn't reference specific chunks, use all chunks
            const relevantChunkIds = referencedChunkIds.length > 0 ? referencedChunkIds : allChunkIds;
            // Build resolved search results with full content
            const resolvedResults = this.resolveSearchResults(relevantChunkIds);
            // Build result for this requirement
            results.push({
                requirementId: req.id,
                relevantResults: {
                    searchResults: resolvedResults,
                },
                rationale: this.extractRationale(responseContent, req.requirement),
            });
            console.log(`[AlphaSenseAgent] Requirement ${req.id}: ${resolvedResults.length} resolved chunks (fallback path)`);
        }
        return results;
    }
    /**
     * Truncate long URLs to reduce token usage
     * Preserves start and end of URL for readability
     */
    truncateUrl(url, maxLength = 80) {
        if (url.length <= maxLength)
            return url;
        const keepStart = Math.floor(maxLength * 0.5);
        const keepEnd = Math.floor(maxLength * 0.3);
        return url.slice(0, keepStart) + '...' + url.slice(-keepEnd);
    }
    /**
     * Remove URLs from markdown links to save tokens
     * Converts [text](url) to text and [[text]](url) to [[text]]
     * Handles optional spaces before the URL parentheses
     */
    stripUrls(text) {
        // Remove URLs from citation links: [[1 • Source] ](https://...) -> [[1 • Source]]
        text = text.replace(/(\[\[[^\]]+\]\])\s*\(https?:\/\/[^)]+\)/g, '$1');
        // Remove URLs from regular markdown links: [text] (https://...) -> text
        text = text.replace(/\[([^\]]+)\]\s*\(https?:\/\/[^)]+\)/g, '$1');
        return text;
    }
    /**
     * Resolve chunk IDs to full content for the orchestrator
     */
    resolveSearchResults(chunkIds) {
        const resolved = [];
        for (const chunkId of chunkIds) {
            const resolvedContent = GlobalStateManager.getInstance().getContext(chunkId);
            const chunk = resolvedContent?.content;
            if (!chunk) {
                console.warn(`[AlphaSenseAgent] Could not resolve chunk: ${chunkId}`);
                continue;
            }
            // Build citations for this chunk with truncated URLs
            /* const citations: Citation[] = (chunk.citationsUsed || []).map(c => ({
                id: c.citationNumber,
                title: c.citationFullTitle,
                url: this.truncateUrl(c.citationUrl),
            })); */
            resolved.push({
                contextId: chunkId,
                text: this.stripUrls(chunk.text),
                // citations removed to save tokens
            });
        }
        return resolved;
    }
    /**
     * Extract chunk context IDs (as_*) mentioned in the LLM response
     */
    extractChunkIdsFromResponse(response, validChunkIds) {
        const mentioned = [];
        // Look for as_* patterns in the response
        const chunkIdPattern = /as_[a-z0-9]+/g;
        const matches = response.match(chunkIdPattern) || [];
        for (const match of matches) {
            // Only include if it's a valid chunk ID from this search
            if (validChunkIds.includes(match) && !mentioned.includes(match)) {
                mentioned.push(match);
            }
        }
        return mentioned;
    }
    /**
     * Extract rationale from response for a specific requirement
     * Returns the full response content since it contains the LLM's analysis
     */
    extractRationale(response, requirement) {
        // Return the full LLM response as the rationale
        // The orchestrator can see the complete analysis
        if (response && response.trim().length > 0) {
            return response.trim();
        }
        return `Searched AlphaSense for: ${requirement}`;
    }
}
// ═══════════════════════════════════════════════════════════════════════════
// EXPORTS
// ═══════════════════════════════════════════════════════════════════════════
export default AlphaSenseAgent;
